import React from "react";
import PropTypes from "prop-types";
import { Icon, Select } from "antd";
import { formatCurrency } from "utilities/currency";
import { COMPANY_DOCUMENT_TILES } from "../constants";

// Same list Case Details tab uses (case-details-summary.js:995) — one row per
// business_product_type enum id, with a human label. Kept here as a local
// constant so the read-only Select in Product Details renders the same
// options as the Case Details tab's editable one.
const PRODUCT_TYPE_DROPDOWN = [
    { id: 1, value: "Invoice Finance" },
    { id: 2, value: "Vendor Finance" },
    { id: 3, value: "Working Capital Demand Loan" },
    { id: 5, value: "Purchase Invoice Finance" },
    { id: 6, value: "Sales Invoice Finance" },
    { id: 8, value: "Personal Term Loan" },
    { id: 9, value: "Short Term Loan (STL)" },
    { id: 10, value: "CEP" }
];

const DASH = "—";

// `editDisabled` keeps the pencil on screen but inert — the affordance stays
// discoverable and its tooltip can explain why it is unavailable, rather than
// the control silently vanishing for users who lack the rights.
function DetailRow({ label, value, tone, onEdit, editTitle, editDisabled }) {
    return (
        <div className={"cc-company__row"}>
            <span className={"cc-company__label"}>{label}</span>
            <span
                className={
                    "cc-company__value" +
                    (value ? "" : " cc-company__value--empty") +
                    (tone ? " cc-company__value--" + tone : "")
                }
            >
                {value || DASH}
                {onEdit ? (
                    <button
                        type={"button"}
                        className={
                            "cc-company__edit" +
                            (editDisabled ? " cc-company__edit--disabled" : "")
                        }
                        onClick={editDisabled ? undefined : onEdit}
                        disabled={Boolean(editDisabled)}
                        title={editTitle || `Change ${label}`}
                    >
                        <Icon type={"edit"} />
                    </button>
                ) : null}
            </span>
        </div>
    );
}

DetailRow.propTypes = {
    label: PropTypes.string,
    value: PropTypes.node,
    tone: PropTypes.string,
    onEdit: PropTypes.func,
    editTitle: PropTypes.string,
    editDisabled: PropTypes.bool
};

// One address with an inline "View utility bill" verification affordance.
function AddressCard({
    label,
    value,
    ownership,
    canVerify,
    onVerify,
    onEdit,
    editTitle,
    wide
}) {
    return (
        <div
            className={
                "cc-company__addr" + (wide ? " cc-company__addr--wide" : "")
            }
        >
            <div className={"cc-company__addr-head"}>
                <span className={"cc-company__addr-label"}>
                    {label}
                    {ownership ? (
                        <span
                            className={
                                "cc-company__own cc-company__own--" +
                                ownership.toLowerCase()
                            }
                        >
                            {ownership}
                        </span>
                    ) : null}
                </span>
                {canVerify ? (
                    <button
                        type={"button"}
                        className={"cc-company__verify"}
                        onClick={onVerify}
                        title={"Open utility bill to verify this address"}
                    >
                        <Icon type={"safety-certificate"} />
                        Verify with utility bill
                    </button>
                ) : null}
                {onEdit ? (
                    <button
                        type={"button"}
                        className={"cc-company__edit"}
                        onClick={onEdit}
                        // "Add" when there is nothing on file yet, mirroring the
                        // Case Summary tab's address card.
                        title={
                            editTitle ||
                            `${value ? "Edit" : "Add"} ${label.toLowerCase()}`
                        }
                    >
                        <Icon type={value ? "edit" : "plus"} />
                    </button>
                ) : null}
            </div>
            <div
                className={
                    "cc-company__addr-value" +
                    (value ? "" : " cc-company__addr-value--empty")
                }
            >
                {value || "Not on file"}
            </div>
        </div>
    );
}

AddressCard.propTypes = {
    label: PropTypes.string,
    value: PropTypes.node,
    ownership: PropTypes.string,
    canVerify: PropTypes.bool,
    onVerify: PropTypes.func,
    onEdit: PropTypes.func,
    editTitle: PropTypes.string,
    wide: PropTypes.bool
};

/**
 * Company identity card: left column carries the underwriting facts (GSTIN, PAN,
 * constitution, vintage, company CIBIL) plus the registered/operating addresses
 * with a "verify with utility bill" link; the right column holds the company
 * verification document chips. A missing document shows a warning-tone "Not on
 * file" state because an absent GST certificate or utility bill is itself a
 * finding.
 */
const CompanyDetailPanel = ({
    company,
    tiles,
    onViewDoc,
    onEditAsm,
    editAsmDisabled,
    editAsmTitle,
    onEditOperatingAddress,
    onEditRegisteredAddress,
    onEditReporting,
    canEditReporting
}) => {
    if (!company) {
        return null;
    }
    const docTiles = tiles || COMPANY_DOCUMENT_TILES;
    const band = company.companyCibilBand || { tone: "muted" };
    const hasUtility = Boolean(company.docs && company.docs.utilityBill);
    const vintage =
        company.vintageYears !== null && company.vintageYears !== undefined
            ? `${company.vintageYears} yr${company.vintageYears === 1 ? "" : "s"}` +
              (company.incorporationLabel
                  ? ` · since ${company.incorporationLabel}`
                  : "")
            : company.incorporationLabel || null;

    // One-panel-per-beat layout: bold section label on the left, fields
    // flow horizontally on the right. Mirrors the Case Details "Case Profile
    // / Product Details / Contact Points" pattern with the same colour cues.
    const cell = (label, value, extra) => (
        <div className={"cc-panel__cell"} key={label}>
            <div className={"cc-panel__cell-label"}>
                {label}
                {extra || null}
            </div>
            <div className={"cc-panel__cell-value"}>{value || DASH}</div>
        </div>
    );
    const reportingEditBtn =
        canEditReporting && onEditReporting ? (
            <button
                type={"button"}
                className={"cc-company__edit"}
                onClick={e => {
                    // Guard against parent handlers swallowing the click —
                    // the coloured panel wrapper doesn't have one today but
                    // stopPropagation makes the button resilient to future
                    // additions.
                    e.stopPropagation();
                    onEditReporting();
                }}
                title={"Edit Reporting Industry / Anchor"}
                style={{ marginLeft: 6, position: "relative", zIndex: 1 }}
            >
                <Icon type={"edit"} />
            </button>
        ) : null;

    return (
        <div className={"cc-company"}>
            <div className={"cc-company__main"}>
                <div className={"cc-panel cc-panel--identity"}>
                    <div className={"cc-panel__title"}>Identity</div>
                    <div className={"cc-panel__body"}>
                        {cell("Legal Name", company.legalName)}
                        {company.tradeName
                            ? cell("Trade Name", company.tradeName)
                            : null}
                        {company.otherTradeName
                            ? cell("Other Trade Name", company.otherTradeName)
                            : null}
                        {cell("GSTIN", company.gstin)}
                        {cell("GSTIN Status", company.gstinStatus)}
                        {cell("Constitution", company.constitution)}
                        {cell("Company PAN", company.pan)}
                    </div>
                </div>
                <div className={"cc-panel cc-panel--case"}>
                    <div className={"cc-panel__title"}>Case Profile</div>
                    <div className={"cc-panel__body"}>
                        {cell("Industry", company.industry)}
                        {cell(
                            "Anchor",
                            company.anchorName ? (
                                <span>
                                    {company.anchorName}
                                    {company.anchorAlignment ? (
                                        <span className={"cc-panel__flag"}>
                                            {" "}
                                            ({company.anchorAlignment})
                                        </span>
                                    ) : null}
                                </span>
                            ) : null
                        )}
                        {/* CIFCL / MF banks show "Product: Vendor" in place of
                            the Seller, matching the base-page Case Profile. */}
                        {["CIFCL", "MF"].includes(company.bank)
                            ? cell("Product", "Vendor")
                            : cell(
                                "Seller",
                                company.sellerName ? (
                                    <span>
                                        {company.sellerName}
                                        {company.sellerAlignment ? (
                                            <span className={"cc-panel__flag"}>
                                                {" "}
                                                ({company.sellerAlignment})
                                            </span>
                                        ) : null}
                                    </span>
                                ) : null
                            )}
                        {cell(
                            "Case Proposed Limit",
                            company.proposedLimit != null
                            && company.proposedLimit !== "" ? (
                                <span>
                                    {formatCurrency(company.proposedLimit, true)}
                                    {company.insuranceApplicable ? (
                                        <b>
                                            {" "}
                                            + {company.insuranceAmount || "--"}{" "}
                                            (Insurance)
                                        </b>
                                    ) : null}
                                </span>
                            ) : null
                        )}
                        {cell(
                            "Bank",
                            company.bank ? (
                                <span title={company.bankName || company.bank}>
                                    {company.bank}
                                </span>
                            ) : null
                        )}
                        {cell("Case Type", company.caseType)}
                        {/* Force State/City/Pincode onto their own row so
                            the address block reads as its own beat inside
                            the Case Profile panel. */}
                        <div className={"cc-panel__break"} />
                        {cell("State", company.state)}
                        {cell("City", company.city)}
                        {cell(
                            "Pincode",
                            company.pincode ? (
                                <span>
                                    {company.pincode}
                                    {/* RAG chip — RED = restricted, AMBER =
                                        caution, GREEN = clean. Source is the
                                        Pincode master's category flag
                                        (UNDEFINED reads GREEN when the pincode
                                        is allowed for onboarding). */}
                                    {company.pincodeCategory === "RED" ? (
                                        <span
                                            className={
                                                "cc-panel__pin cc-panel__pin--red"
                                            }
                                        >
                                            {" "}
                                            Red
                                        </span>
                                    ) : company.pincodeCategory
                                          === "AMBER" ? (
                                        <span
                                            className={
                                                "cc-panel__pin cc-panel__pin--amber"
                                            }
                                        >
                                            {" "}
                                            Amber
                                        </span>
                                    ) : company.pincodeCategory
                                          === "GREEN" ? (
                                        <span
                                            className={
                                                "cc-panel__pin cc-panel__pin--green"
                                            }
                                        >
                                            {" "}
                                            Green
                                        </span>
                                    ) : null}
                                </span>
                            ) : null
                        )}
                    </div>
                </div>
                <div className={"cc-panel cc-panel--product"}>
                    <div className={"cc-panel__title"}>Product Details</div>
                    <div className={"cc-panel__body"}>
                        {cell("ROI", company.roi)}
                        {cell("Tenure", company.tenure)}
                        {cell("Scheme Type", company.schemeType)}
                        {/* Product Type + Program Type render as plain text
                            like every other cell in this card — the "pill /
                            dropdown" treatment made them stand out and hinted
                            at editability, which they aren't here (edits
                            happen on the Case Details tab). */}
                        {cell("Product Type", company.productType)}
                        {cell("Program Type", company.programType)}
                    </div>
                </div>
                <div className={"cc-panel cc-panel--contact"}>
                    <div className={"cc-panel__title"}>Contact Points</div>
                    <div className={"cc-panel__body"}>
                        {cell(
                            "ASM",
                            company.asm,
                            onEditAsm ? (
                                <button
                                    type={"button"}
                                    className={
                                        "cc-company__edit"
                                        + (editAsmDisabled
                                            ? " cc-company__edit--disabled"
                                            : "")
                                    }
                                    onClick={
                                        editAsmDisabled
                                            ? undefined
                                            : e => {
                                                e.stopPropagation();
                                                onEditAsm();
                                            }
                                    }
                                    disabled={Boolean(editAsmDisabled)}
                                    title={editAsmTitle || "Change ASM"}
                                    style={{
                                        marginLeft: 6,
                                        position: "relative",
                                        zIndex: 1
                                    }}
                                >
                                    <Icon type={"edit"} />
                                </button>
                            ) : null
                        )}
                        {cell("DRM", company.rm)}
                        {cell(
                            "Reporting Industry",
                            company.reportingIndustry,
                            reportingEditBtn
                        )}
                        {cell(
                            "Reporting Anchor",
                            company.reportingAnchor,
                            reportingEditBtn
                        )}
                    </div>
                </div>
                <div className={"cc-panel cc-panel--credit"}>
                    {/* "Business Health" captures all three fields in this
                        panel — Business Vintage (age of business), Last GST
                        Return Filed (compliance), Entity CIBIL (credit
                        standing) — better than the earlier "Credit Snapshot"
                        which only fit the last of the three. */}
                    <div className={"cc-panel__title"}>Business Health</div>
                    <div className={"cc-panel__body"}>
                        {cell("Business Vintage", vintage)}
                        {/* Anchor-appointment date lives here rather than
                            in Case Profile because it's a proxy for the
                            seller's tenure with the anchor — a health /
                            longevity signal, not a case classifier. */}
                        {cell(
                            "Anchor Appointment",
                            company.anchorAppointmentLabel
                        )}
                        {cell(
                            "Last GST Return Filed",
                            company.gstinFilingLabel
                        )}
                        {cell(
                            "Entity CIBIL",
                            company.companyCibil ? (
                                <span
                                    className={
                                        "cc-kyc-cibiltag cc-kyc-cibiltag--"
                                        + band.tone
                                    }
                                >
                                    {company.companyCibil}
                                </span>
                            ) : null
                        )}
                    </div>
                </div>
                {/* Legacy facts grid retained as a hidden anchor — nothing renders
                    because all fields have moved up into the coloured panels. */}
                <div className={"cc-company__facts"} style={{ display: "none" }}>
                    <div className={"cc-company__row"}>
                        <span className={"cc-company__label"}>Entity CIBIL</span>
                        <span className={"cc-company__value"}>
                            {company.companyCibil ? (
                                <span
                                    className={
                                        "cc-kyc-cibiltag cc-kyc-cibiltag--" +
                                        band.tone
                                    }
                                >
                                    {company.companyCibil}
                                </span>
                            ) : (
                                <span className={"cc-company__value--empty"}>
                                    {DASH}
                                </span>
                            )}
                        </span>
                    </div>
                </div>

                <div className={"cc-company__addresses"}>
                    <AddressCard
                        label={"Registered address"}
                        value={
                            company.registeredAddress &&
                            company.registeredAddress.text
                        }
                        ownership={
                            company.registeredAddress &&
                            company.registeredAddress.ownership
                        }
                        canVerify={hasUtility}
                        onVerify={() => onViewDoc && onViewDoc("utilityBill")}
                        onEdit={onEditRegisteredAddress}
                    />
                    <AddressCard
                        label={"Operating address"}
                        value={
                            company.operatingAddress &&
                            company.operatingAddress.text
                        }
                        ownership={
                            company.operatingAddress &&
                            company.operatingAddress.ownership
                        }
                        canVerify={hasUtility}
                        onVerify={() => onViewDoc && onViewDoc("utilityBill")}
                        onEdit={onEditOperatingAddress}
                    />
                    {/* Any further addresses on file — one card, bulleted, since
                        there can be several. Hidden entirely when there are none. */}
                    {(company.additionalAddresses || []).length ? (
                        <AddressCard
                            wide
                            label={"Additional address"}
                            value={
                                <ul className={"cc-company__addr-list"}>
                                    {company.additionalAddresses.map(addr => (
                                        <li key={addr.key}>{addr.text}</li>
                                    ))}
                                </ul>
                            }
                        />
                    ) : null}
                </div>
            </div>

            <div className={"cc-company__docs"}>
                <div className={"cc-company__docs-title"}>
                    Verification documents
                </div>
                <div className={"cc-company__chips"}>
                    {docTiles.map(tile => {
                        const present = Boolean(
                            company.docs && company.docs[tile.key]
                        );
                        return (
                            <button
                                type={"button"}
                                key={tile.key}
                                className={
                                    "cc-company__chip" +
                                    (present
                                        ? ""
                                        : " cc-company__chip--missing")
                                }
                                onClick={() =>
                                    present && onViewDoc && onViewDoc(tile.key)
                                }
                                disabled={!present}
                                title={
                                    present
                                        ? `View ${tile.label}`
                                        : `${tile.label} not on file`
                                }
                            >
                                <span className={"cc-company__chip-icon"}>
                                    <Icon type={tile.icon} />
                                </span>
                                <span className={"cc-company__chip-body"}>
                                    <span className={"cc-company__chip-label"}>
                                        {tile.label}
                                    </span>
                                    <span className={"cc-company__chip-state"}>
                                        {present ? "On file" : "Not on file"}
                                    </span>
                                </span>
                                {present ? (
                                    <Icon
                                        type={"eye"}
                                        className={"cc-company__chip-view"}
                                    />
                                ) : (
                                    <Icon
                                        type={"warning"}
                                        className={"cc-company__chip-warn"}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

CompanyDetailPanel.propTypes = {
    company: PropTypes.object,
    tiles: PropTypes.array,
    onViewDoc: PropTypes.func,
    onEditAsm: PropTypes.func,
    editAsmDisabled: PropTypes.bool,
    editAsmTitle: PropTypes.string,
    onEditOperatingAddress: PropTypes.func,
    onEditRegisteredAddress: PropTypes.func,
    onEditReporting: PropTypes.func,
    canEditReporting: PropTypes.bool
};

export default CompanyDetailPanel;
